<template>
  <span
    contenteditable="false"
    @mouseover="showHint = true"
    @mouseleave="showHint = false"
  >
    <span v-if="kind === undefined">{{ node.attrs.title }}</span>
    <component
      :is="notFound ? 'span' : 'router-link'"
      v-else
      :class="['resource-mention-component', notFound ? '-not-found' : '']"
      :to="notFound ? undefined : kind.to(node.attrs.id)"
    >
      <span class="_icon-wrapper">
        <m-icon
          size="calc(1em - 1px)"
          :type="kind.icon"
          class="_icon"
        />
      </span>
      <span class="_title">
        <span
          v-if="notFound"
          v-show="showHint"
          class="_hint"
        >
          {{ $t(kind.hint) }}
        </span>
        <span
          v-if="!isEmptyIcon(icon)"
          class="_emoji"
        >
          <m-icon-display
            class="_emoji-inner"
            :icon="icon"
          /></span>{{ title === '' ? $t('general.untitled') : title }}
      </span>
      <span style="white-space: nowrap;" />
    </component>
  </span>
</template>

<script setup>
import useRepo from '@/nebula/repo';
import { buildIconFromEntity, isEmptyIcon } from 'shared/lib/icon';
import { computed, ref } from 'vue';
import { gridPage as gridPageConfig, space as spaceConfig } from 'shared/api/query/configs.json';
import { routeName } from 'shared/constants.json';

defineOptions({ inheritAttrs: false });

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
});

const kinds = {
  [spaceConfig.model]: {
    icon: 'team',
    hint: 'resourceMentionComponent.spaceHint',
    entities: useRepo(spaceConfig.model).entityList,
    to: (optionId) => ({ name: routeName.spaceDetails, params: { optionId } }),
  },
  [gridPageConfig.model]: {
    icon: 'appstore',
    hint: 'resourceMentionComponent.dashboardHint',
    entities: useRepo(gridPageConfig.model).entityList,
    to: (pageId) => ({ name: routeName.dashboardDetails, params: { pageId } }),
  },
};

const kind = computed(() => kinds[props.node.attrs.kind]);
const entity = computed(() => kind.value?.entities.value.find((e) => e.uid === props.node.attrs.id));
const title = computed(() => entity.value?.title ?? props.node.attrs.title);
const icon = computed(() => buildIconFromEntity(entity.value));
const notFound = computed(() => entity.value === undefined);
const showHint = ref(false);
</script>

<style scoped lang="scss" type="text/scss">
  .resource-mention-component {
    position: relative;
    padding-left: 1.2em;
    color: inherit;
    text-decoration: inherit;
    vertical-align: top;
    cursor: pointer;
    border-radius: $border-radius-xs;
    transition: all 100ms ease-in 0s;

    &:hover {
      background: $hover-color;
      box-shadow: 0 0 0 4px $hover-color;
    }

    ._icon-wrapper {
      position: absolute;
      top: -.15em;
      left: .1em;
    }

    ._icon {
      display: inline-block;
      white-space: nowrap;
    }

    ._title {
      position: relative;
      font-weight: $font-weight-medium;
      border-bottom: .06em solid $font-color-primary;

      ._hint {
        position: absolute;
        bottom: calc(100% + .6rem);
        left: 50%;
        width: 20rem;
        padding: .4rem;
        font-size: $font-size-2;
        color: white;
        white-space: initial;
        background-color: map_get($grey, 'darken-4');
        border-radius: $tooltip-border-radius;
        transform: translateX(-50%);
      }

      ._emoji {
        display: inline-block;
        margin-right: .4rem;
        font-size: 1em;
        vertical-align: baseline;
      }
    }

    &.-not-found {
      color: $font-color-tertiary;
      cursor: default;

      &:hover {
        background: none;
        box-shadow: none;
      }

      ._title {
        border-color: $font-color-tertiary;
      }
    }
  }
</style>
